/**
 * Google Play Billing via Digital Goods API (TWA only).
 *
 * SKUs (create in Play Console → Subscriptions):
 *   un8.member.monthly  — $4.99/mo, 14-day free trial
 *   un8.vip.monthly     — $9.99/mo, 14-day free trial
 *
 * Outside a Play-launched TWA, getDigitalGoodsService is unavailable —
 * never fall back to Stripe / web checkout. QA activate* stays for closed test.
 */

import { billingStore, type PaidTier } from "./billingStore";

export const PLAY_SKU_MEMBER = "un8.member.monthly";
export const PLAY_SKU_VIP = "un8.vip.monthly";
export const PLAY_BILLING_METHOD = "https://play.google.com/billing";

const PURCHASE_KEY = "un8.billing.playPurchases.v1";

type PlayPurchaseRecord = {
  sku: string;
  purchaseToken: string;
  acknowledgedAt: string;
};

declare global {
  interface Window {
    getDigitalGoodsService?: (
      paymentMethod: string
    ) => Promise<DigitalGoodsService | null>;
  }
}

/** Minimal Digital Goods surface (Chrome Android / TWA). */
type DigitalGoodsService = {
  getDetails: (itemIds: string[]) => Promise<Array<{ itemId: string; title?: string; price?: { value: string; currency: string } }>>;
  listPurchases: () => Promise<Array<{ itemId: string; purchaseToken: string }>>;
  acknowledge?: (purchaseToken: string, purchaseType?: string) => Promise<void>;
};

function tierForSku(sku: string): PaidTier | null {
  if (sku === PLAY_SKU_MEMBER) return "subscriber";
  if (sku === PLAY_SKU_VIP) return "vip";
  return null;
}

function readPurchases(): PlayPurchaseRecord[] {
  try {
    const raw = localStorage.getItem(PURCHASE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as PlayPurchaseRecord[];
  } catch {
    return [];
  }
}

function writePurchases(list: PlayPurchaseRecord[]) {
  localStorage.setItem(PURCHASE_KEY, JSON.stringify(list));
}

function rememberPurchase(sku: string, purchaseToken: string) {
  const list = readPurchases().filter((p) => p.purchaseToken !== purchaseToken);
  list.push({ sku, purchaseToken, acknowledgedAt: new Date().toISOString() });
  writePurchases(list);
}

export function playBillingAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.getDigitalGoodsService === "function";
}

async function getService(): Promise<DigitalGoodsService | null> {
  if (!playBillingAvailable() || !window.getDigitalGoodsService) return null;
  try {
    return await window.getDigitalGoodsService(PLAY_BILLING_METHOD);
  } catch {
    return null;
  }
}

export type PlayPurchaseResult =
  | { ok: true; tier: PaidTier; sku: string }
  | { ok: false; error: "unavailable" | "cancelled" | "failed"; detail?: string };

/**
 * Launch Play purchase sheet for Member or VIP.
 * Trial length is configured on the Play Console SKU (14 days), not in this client.
 */
export async function purchasePlaySku(
  userId: string | null,
  sku: typeof PLAY_SKU_MEMBER | typeof PLAY_SKU_VIP
): Promise<PlayPurchaseResult> {
  const tier = tierForSku(sku);
  if (!tier) return { ok: false, error: "failed", detail: "Unknown SKU" };
  if (!userId) return { ok: false, error: "failed", detail: "Sign in first." };

  const service = await getService();
  if (!service) return { ok: false, error: "unavailable" };

  try {
    await service.getDetails([sku]);
  } catch (err) {
    return {
      ok: false,
      error: "failed",
      detail: err instanceof Error ? err.message : "SKU not found in Play Console",
    };
  }

  try {
    const request = new PaymentRequest(
      [
        {
          supportedMethods: PLAY_BILLING_METHOD,
          data: { sku },
        },
      ],
      {
        total: {
          label: tier === "vip" ? "UN8 VIP" : "UN8 Member",
          amount: { currency: "USD", value: "0" },
        },
      }
    );

    const canPay = await request.canMakePayment();
    if (!canPay) return { ok: false, error: "unavailable" };

    const response = await request.show();
    const details = (response.details || {}) as {
      purchaseToken?: string;
      token?: string;
    };
    const purchaseToken = details.purchaseToken || details.token || "";
    await response.complete("success");

    if (purchaseToken && typeof service.acknowledge === "function") {
      try {
        await service.acknowledge(purchaseToken, "onetime");
      } catch {
        /* subscription ack may use different purchaseType — ignore */
      }
    }

    if (purchaseToken) rememberPurchase(sku, purchaseToken);
    billingStore.applyPlayPurchase(userId, tier);
    return { ok: true, tier, sku };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/abort|cancel/i.test(msg)) return { ok: false, error: "cancelled" };
    return { ok: false, error: "failed", detail: msg };
  }
}

/** Re-apply entitlements from Play listPurchases (reinstall / new device). */
export async function restorePlayPurchases(
  userId: string | null
): Promise<{ ok: true; restored: PaidTier | null } | { ok: false; error: string }> {
  if (!userId) return { ok: false, error: "Sign in first." };
  const service = await getService();
  if (!service) return { ok: false, error: "unavailable" };

  try {
    const purchases = await service.listPurchases();
    let best: PaidTier | null = null;
    for (const p of purchases) {
      const tier = tierForSku(p.itemId);
      if (!tier) continue;
      rememberPurchase(p.itemId, p.purchaseToken);
      if (tier === "vip") best = "vip";
      else if (tier === "subscriber" && best !== "vip") best = "subscriber";
    }
    if (best) billingStore.applyPlayPurchase(userId, best);
    return { ok: true, restored: best };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Restore failed",
    };
  }
}
